import { Control, Controller, FieldError, FieldValues, Path } from 'react-hook-form'
import { FormControl, FormHelperText, ToggleButtonGroup, ToggleButton, SxProps, Theme } from '@mui/material'

type Props<T extends FieldValues> = {
  sx?: SxProps<Theme>
  control: Control<T>
  error: FieldError | undefined
  fieldName: Path<T>
  firstOptionLabel: string
  secondOptionLabel: string
  firstOptionValue: string | boolean | number
  secondOptionValue: string | boolean | number
  requiredRule: string | undefined
  disabled?: boolean
  fullWidth?: boolean
  onKeyUp?: () => void
  onChangeFromParent?: () => void
}

export function ControlledToggleButton<T extends FieldValues>({
  sx,
  control,
  error,
  fieldName,
  firstOptionLabel,
  secondOptionLabel,
  firstOptionValue,
  secondOptionValue,
  requiredRule,
  disabled,
  onKeyUp,
  onChangeFromParent,
  fullWidth = false,
}: Props<T>) {
  return (
    <FormControl
      fullWidth={fullWidth}
      sx={{ width: '100%', ...sx }}
      required={Boolean(requiredRule)}
      component="fieldset"
    >
      <Controller
        name={fieldName}
        control={control}
        defaultValue={undefined}
        rules={{
          // Custom validate function to correctly handle `false` as a valid value
          validate: (value) => value !== undefined || requiredRule,
        }}
        render={({ field: { onChange, value } }) => (
          <ToggleButtonGroup
            value={value}
            exclusive
            onChange={(_, newValue) => {
              if (newValue !== null) {
                onChange(newValue)
              }
              if (onChangeFromParent) {
                onChangeFromParent()
              }
            }}
            size="medium"
            disabled={disabled}
            onKeyUp={onKeyUp}
            fullWidth={fullWidth}
            sx={{ '& .MuiToggleButton-root': { height: '48px' } }} // Set height of buttons
          >
            <ToggleButton value={firstOptionValue} key="firstOption">
              {firstOptionLabel}
            </ToggleButton>
            <ToggleButton value={secondOptionValue} key="secondOption">
              {secondOptionLabel}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      />
      <FormHelperText error={!!error}>{error?.message}</FormHelperText>
    </FormControl>
  )
}
